/**
 * SquareAIEditor
 *
 * Workflow node editor for the "Square AI" action.
 *
 * Architecture:
 *   ① AI Config      — which LLM + system prompt
 *   ② Square Status  — confirms Square is connected (single config per site)
 *   ③ MCP Servers    — tools the AI can call
 *   ④ Scenario Prompts — copy/paste snippets for the AI Config system prompt
 *   ⑤ Specialists    — optional delegation
 *
 * Square live tools (messaging skills):
 *   - square_get_customer, square_create_customer, square_update_customer
 *   - square_get_order, square_search_orders, square_cancel_order
 *   - square_check_availability, square_book_appointment, square_reschedule_appointment, square_cancel_appointment
 *   - square_search_catalog, square_get_catalog_item, square_check_inventory
 *   - square_get_loyalty_balance, square_get_loyalty_rewards, square_redeem_loyalty_reward
 *   - square_get_payment_history, square_create_checkout_link, square_list_locations
 */
import * as React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useSquareStatus from "@/hooks/useSquareStatus";
import useMcpServers from "@/hooks/useMcpServers";
import AISetupGuide from "./AISetupGuide";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import AIContextInspector from "./AIContextInspector";
import McpStaleBadge from "./McpStaleBadge";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

const SQUARE_SCENARIOS = [
  {
    id: "order_lookup",
    label: "Order Lookup",
    description: "Customer asks about a recent order or order status.",
    prompt: `When a customer asks about an order, use square_get_order with the order ID or square_search_orders with their email. Return the order status, items, total, and fulfillment details. If unfulfilled, give an honest timeline.`
  },
  {
    id: "appointment_booking",
    label: "Appointment Booking",
    description: "Customer wants to book, reschedule, or cancel a service appointment.",
    prompt: `When a customer wants to book an appointment, use square_check_availability to find open slots, then square_book_appointment to confirm. For rescheduling use square_reschedule_appointment; for cancellations use square_cancel_appointment. Always confirm the location, service, and team member preference first.`
  },
  {
    id: "product_catalog",
    label: "Product & Catalog Inquiry",
    description: "Customer asks about products, services, or pricing.",
    prompt: `When a customer asks about products or services, use square_search_catalog with relevant keywords. Return the item name, price, description, and availability. Use square_check_inventory to verify stock before confirming availability.`
  },
  {
    id: "loyalty",
    label: "Loyalty & Rewards",
    description: "Customer asks about their loyalty points or available rewards.",
    prompt: `When a customer asks about loyalty points or rewards, use square_get_loyalty_balance to show their current points, then square_get_loyalty_rewards to list available rewards. If they want to redeem, use square_redeem_loyalty_reward and confirm the redemption details.`
  },
  {
    id: "account_lookup",
    label: "Customer Account Lookup",
    description: "Customer wants to know their account info or purchase history.",
    prompt: `When a customer asks about their account, use square_get_customer with their email or phone. Summarize their profile, recent orders via square_search_orders, and loyalty status via square_get_loyalty_balance. If no account exists, offer to create one with square_create_customer.`
  },
  {
    id: "checkout",
    label: "Checkout & Payment Links",
    description: "Customer needs help completing a purchase or wants a payment link.",
    prompt: `When a customer is ready to purchase and needs a payment link, use square_create_checkout_link with the items and amount. Share the link and guide them through the checkout process. Confirm the location using square_list_locations if needed.`
  }
];

export default function SquareAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { status: squareStatus, loading: squareLoading } = useSquareStatus();
  const { items: mcpServers } = useMcpServers();
  const [copiedScenario, setCopiedScenario] = useState<string | null>(null);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));
  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName =
    (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];
  const enabledToolNames = enabledMcpServers
    .filter((s) => selectedMcpIds.includes(s.id))
    .flatMap((s) => s.toolNames ?? []);

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  async function copyScenarioPrompt(id: string, prompt: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedScenario(id);
      setTimeout(() => setCopiedScenario(null), 2000);
    } catch { /* graceful fallback */ }
  }

  const squareConnected = !squareLoading && squareStatus?.configured === true;

  return (
    <>
      <AISetupGuide
        dataSourceName="Square"
        dataSourcePath="/integrations/square"
        hasAiConfig={!!selectedAiConfig}
        hasMcpServers={enabledMcpServers.length > 0}
        hasDataSourceConfig={squareConnected}
      />

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found. <Link to="/bots" className="underline font-medium">Create one first →</Link>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            No system prompt yet. <Link to="/bots" className="underline">Add one →</Link> Use the Scenario Prompts below.
          </p>
        )}
      </FormGroup>

      {/* ── Step 2: Square Connection Status ── */}
      <FormGroup label="Square Account">
        {squareLoading ? (
          <div className="text-xs text-slate-400 px-3 py-2">Checking Square connection…</div>
        ) : squareConnected ? (
          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
            ✓ Square connected
            {squareStatus?.merchantName && (
              <span className="text-slate-500 ml-1">— {squareStatus.merchantName}</span>
            )}
            {squareStatus?.locationName && (
              <span className="text-slate-500 ml-1">({squareStatus.locationName})</span>
            )}
          </div>
        ) : (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Square is not connected.{" "}
            <Link to="/integrations/square" className="underline font-medium">Connect Square →</Link>
            <p className="mt-1 text-slate-500">
              Square credentials allow the AI to look up orders, appointments, catalog, and loyalty accounts.
            </p>
          </div>
        )}
      </FormGroup>

      {/* ── Step 3: MCP Tool Servers ── */}
      <FormGroup label="MCP Tool Servers">
        {enabledMcpServers.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            No MCP tool servers configured.{" "}
            <Link to="/integrations/mcp" className="text-blue-600 underline">Add an MCP server →</Link>
            <p className="mt-1">
              MCP servers give the AI tools:{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">square_get_order</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">square_book_appointment</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">square_search_catalog</code>, etc.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-1">
            {providerUnsupported && (
              <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded border border-amber-200">
                MCP tools require Claude or OpenAI. The selected AI config may not support them.
              </p>
            )}
            {enabledMcpServers.map((server) => {
              const checked = selectedMcpIds.includes(server.id);
              return (
                <label
                  key={server.id}
                  className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                    checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 shrink-0"
                    checked={checked}
                    onChange={(e) => toggleMcpServer(server.id, e.target.checked)}
                    aria-label={`Enable MCP server: ${server.name}`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{server.name}</span>
                      {server.toolCount > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                          {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                        </span>
                      )}
                      <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                    </div>
                    {server.description && <span className="text-xs text-slate-500">{server.description}</span>}
                    {server.toolNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {server.toolNames.slice(0, 6).map((t) => (
                          <span key={t} className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{t}</span>
                        ))}
                        {server.toolNames.length > 6 && (
                          <span className="text-xs text-slate-400 px-1 py-0.5">+{server.toolNames.length - 6} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </FormGroup>

      {/* ── Scenario Prompts ── */}
      <div className="mt-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Scenario Prompts</span>
          <span className="text-xs text-slate-400 italic">for your AI Config</span>
        </div>
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          Copy these into your AI Config's system prompt. Customize the bracketed sections for your business.
        </p>
        <div className="flex flex-col gap-2">
          {SQUARE_SCENARIOS.map((scenario) => {
            const isExpanded = expandedScenario === scenario.id;
            const isCopied = copiedScenario === scenario.id;
            return (
              <div key={scenario.id} className="border border-slate-200 rounded bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedScenario(isExpanded ? null : scenario.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div>
                    <span className="text-xs font-medium text-slate-800">{scenario.label}</span>
                    {!isExpanded && <p className="text-xs text-slate-400 mt-0.5">{scenario.description}</p>}
                  </div>
                  <span className="text-slate-400 text-xs ml-2 shrink-0">{isExpanded ? "▲" : "▼"}</span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{scenario.description}</p>
                    <pre className="text-xs bg-slate-900 text-slate-100 rounded p-2.5 whitespace-pre-wrap leading-relaxed font-mono mb-2">
                      {scenario.prompt}
                    </pre>
                    <button
                      type="button"
                      onClick={() => copyScenarioPrompt(scenario.id, scenario.prompt)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        isCopied ? "border-green-300 bg-green-50 text-green-700" : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isCopied ? "✓ Copied!" : "Copy prompt snippet"}
                    </button>
                    <p className="text-xs text-slate-400 mt-1.5">Paste into your AI Config's system prompt, then customize the bracketed sections.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <AIContextInspector mcpToolNames={enabledToolNames} />

      <div className="mt-4 pt-3 border-t border-slate-200">
        <AISpecialistSection node={node} onUpdate={onUpdate} aiConfigurations={aiConfigurations} enabledMcpServers={enabledMcpServers} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
